import React from "react"
import { Controller, UseFormWatch } from "react-hook-form"
import { Control, UseFormSetValue } from "react-hook-form/dist/types/form"
import { DiscountRule, InputCreateOrUpdateDiscountRule } from "lib/types/generated/graphql-types"
import { FieldErrors } from "react-hook-form/dist/types/errors"

import { CheckboxWrapper, Container, Name, SortOrder } from "./DiscountRuleGeneralInput.styled"

import Input from "components/Ui/Form/Input"
import Checkbox from "components/Ui/Form/Checkbox"
import DisplayNameInput from "./DisplayNames/DisplayNameInput"
import TimePeriodSelectionDiscountRules from "./TimePeriodSelectionDiscountRules"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import FallbackInput from "../../Ui/Form/FallbackInput"
import FallbackCheckbox from "../../Ui/Form/FallbackCheckbox"

type Props = {
  cartRule: DiscountRule
  control: Control<InputCreateOrUpdateDiscountRule>
  errors: FieldErrors<InputCreateOrUpdateDiscountRule>
  setValue: UseFormSetValue<InputCreateOrUpdateDiscountRule>
  watch: UseFormWatch<InputCreateOrUpdateDiscountRule>
}

const CartRuleGeneralUpdateInput = ({ cartRule, control, errors, setValue, watch }: Props) => {
  if (!cartRule) return null

  return (
    <div>
      <Container>
        <div>
          <h2>General</h2>
          <p>General inputs for discount rule applicable in cart without code</p>
          <Name>
            <WithAuth
              requiredGroups={PermissionGroups.discounts.write}
              fallback={<FallbackInput label={"Name"} value={cartRule.name} />}
            >
              <Controller
                name="name"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    label="Name *"
                    placeholder="Aa - Zz, 0 - 9"
                    errors={errors}
                  />
                )}
                control={control}
                defaultValue={cartRule.name}
                rules={{
                  required: "This is a required field"
                }}
              />
            </WithAuth>
          </Name>
          <SortOrder>
            <WithAuth
              requiredGroups={PermissionGroups.discounts.write}
              fallback={<FallbackInput label={"Sort order"} value={cartRule.sortOrder.toString()} />}
            >
              <Controller
                name="sortOrder"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    label="Sort order *"
                    placeholder="Minimum value is 1"
                    errors={errors}
                  />
                )}
                control={control}
                defaultValue={cartRule.sortOrder}
                rules={{
                  required: "This is a required field",
                  min: { value: 1, message: "Minimum value is 1" }
                }}
              />
            </WithAuth>
          </SortOrder>
          <CheckboxWrapper>
            <WithAuth requiredGroups={PermissionGroups.discounts.write} fallback={<FallbackCheckbox label={"Active"} value={cartRule.isActive}/>}>
              <Checkbox
                control={control}
                name="isActive"
                label="Active"
                defaultValue={cartRule.isActive}
              />
            </WithAuth>
            <WithAuth requiredGroups={PermissionGroups.discounts.write} fallback={<FallbackCheckbox label={"Is stackable"} description={"Can this rule stack with other rules?"} value={cartRule.isStackable}/>}>
              <Checkbox
                control={control}
                name="isStackable"
                label="Is stackable"
                description="Can this rule stack with other rules?"
                defaultValue={cartRule.isStackable}
              />
            </WithAuth>

          </CheckboxWrapper>
        </div>
        <div>
          <DisplayNameInput
            setValue={setValue}
            watch={watch}
            savedDisplayNames={cartRule?.displayNames ?? []}
          />
        </div>
      </Container>
      <hr />
      <WithAuth requiredGroups={PermissionGroups.discounts.write} fallback={<FallbackInput label={"Time period limitation"} dateTime={cartRule.validDateRange ?? undefined}/>}>
        <TimePeriodSelectionDiscountRules
          control={control}
          errors={errors}
          defaultValues={cartRule.validDateRange ?? undefined}
        />
      </WithAuth>

    </div>
  )
}

export default CartRuleGeneralUpdateInput
